import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactForm = ({ defaultSubject = '' }) => {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: '',
    email: '',
    subject: defaultSubject,
    message: '',
  });
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const onChange = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const canSubmit =
    consent &&
    form.name.trim().length > 0 &&
    form.email.trim().length > 0 &&
    form.message.trim().length > 0 &&
    status !== 'loading';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus('loading');
    setErrorMsg('');
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim() || null,
        message: form.message.trim(),
        consent_given: true,
      });
      setStatus('success');
      setForm({ name: '', email: '', subject: '', message: '' });
      setConsent(false);
    } catch (err) {
      setStatus('error');
      setErrorMsg(
        err?.response?.data?.detail ||
          t(
            'Er ging iets mis. Probeer het later opnieuw.',
            'Something went wrong. Please try again later.'
          )
      );
    }
  };

  const labelClass = 'block text-sm font-medium text-gray-800 mb-1.5';
  const inputClass =
    'w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-shadow';

  if (status === 'success') {
    return (
      <div
        className="bg-gradient-to-br from-pink-50 via-purple-50 to-indigo-50 border border-purple-100 rounded-2xl p-10 text-center"
        data-testid="contact-form-success"
      >
        <h3 className="text-2xl font-light text-gray-900 mb-3">
          {t('Bedankt voor je bericht.', 'Thanks for your message.')}
        </h3>
        <p className="text-gray-600 leading-relaxed max-w-md mx-auto">
          {t(
            'We hebben je bericht goed ontvangen en nemen zo snel mogelijk contact met je op.',
            "We've received your message and will get back to you as soon as possible."
          )}
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6 sm:p-8 space-y-5"
      data-testid="contact-form"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className={labelClass} htmlFor="cf-name">
            {t('Je naam', 'Your name')} <span className="text-pink-500">*</span>
          </label>
          <input
            id="cf-name"
            type="text"
            required
            value={form.name}
            onChange={onChange('name')}
            className={inputClass}
            data-testid="contact-name"
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="cf-email">
            {t('E-mailadres', 'Email')} <span className="text-pink-500">*</span>
          </label>
          <input
            id="cf-email"
            type="email"
            required
            value={form.email}
            onChange={onChange('email')}
            className={inputClass}
            data-testid="contact-email"
          />
        </div>
      </div>
      <div>
        <label className={labelClass} htmlFor="cf-subject">
          {t('Onderwerp', 'Subject')}
        </label>
        <input
          id="cf-subject"
          type="text"
          value={form.subject}
          onChange={onChange('subject')}
          placeholder={t('Waar gaat je bericht over?', "What is your message about?")}
          className={inputClass}
          data-testid="contact-subject"
        />
      </div>
      <div>
        <label className={labelClass} htmlFor="cf-message">
          {t('Bericht', 'Message')} <span className="text-pink-500">*</span>
        </label>
        <textarea
          id="cf-message"
          required
          rows={5}
          value={form.message}
          onChange={onChange('message')}
          className={inputClass}
          data-testid="contact-message"
        />
      </div>

      <label className="flex items-start gap-3 text-sm text-gray-600 cursor-pointer">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-1 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
          data-testid="contact-consent"
        />
        <span>
          {t(
            'Ik ga akkoord met de verwerking van mijn gegevens om mijn bericht te beantwoorden, zoals beschreven in het ',
            'I agree to the processing of my data to respond to my message, as described in the '
          )}
          <Link to="/privacy" className="text-purple-600 underline hover:text-purple-800">
            {t('privacybeleid', 'privacy policy')}
          </Link>
          .
        </span>
      </label>

      <div className="pt-2">
        <button
          type="submit"
          disabled={!canSubmit}
          className="w-full sm:w-auto bg-gray-900 text-white px-8 py-3.5 rounded-full font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="contact-submit"
        >
          {status === 'loading'
            ? t('Bezig met versturen…', 'Sending…')
            : t('Verstuur bericht', 'Send message')}
        </button>
        {status === 'error' && (
          <p className="text-sm text-red-600 mt-3" data-testid="contact-error">
            {errorMsg}
          </p>
        )}
      </div>
    </form>
  );
};

export default ContactForm;
